import { Component, Input, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { NbDialogRef, NbToastrService } from '@nebular/theme';
import { TranslateService } from '@ngx-translate/core';
import { ReferralPartner, ReferralService } from '../../../services/referral.service';

/** Partner master data for the Gutschrift + payout settings. super_admin only. */
@Component({
  selector: 'app-referral-partner-dialog',
  templateUrl: './referral-partner-dialog.component.html',
  styleUrl: './referral-partner-dialog.component.scss',
})
export class ReferralPartnerDialogComponent implements OnInit {
  /** Undefined = create a new partner. */
  @Input() partner?: ReferralPartner;

  form!: FormGroup;
  saving = false;
  /** Set when the name is taken: offer replace vs. rename. */
  conflict?: { message: string; existingId: string };
  readonly vatModes = ['regular', 'small_business', 'reverse_charge'];
  readonly billingModes = ['self_billing', 'partner_invoice'];
  readonly payoutModes = ['manual', 'auto'];

  constructor(
    protected dialogRef: NbDialogRef<ReferralPartnerDialogComponent>,
    private fb: FormBuilder,
    private referralService: ReferralService,
    private toastr: NbToastrService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    const p: Partial<ReferralPartner> = this.partner ?? { vat_mode: 'regular', billing_mode: 'self_billing', payout_mode: 'manual', payout_day: 15, country: 'DE' };
    this.form = this.fb.group({
      name: [p.name ?? '', Validators.required],
      legal_name: [p.legal_name ?? ''],
      email: [p.email ?? '', Validators.email],
      street: [p.street ?? ''],
      zip: [p.zip ?? ''],
      city: [p.city ?? ''],
      country: [p.country || 'DE'],
      tax_id: [p.tax_id ?? ''],
      contract_reference: [p.contract_reference ?? ''],
      vat_mode: [p.vat_mode ?? 'regular', Validators.required],
      billing_mode: [p.billing_mode || 'self_billing', Validators.required],
      payout_mode: [p.payout_mode || 'manual', Validators.required],
      payout_day: [p.payout_day || 15, [Validators.required, Validators.min(1), Validators.max(28)]],
      billing_via: [p.billing_via || 'alphalearn', Validators.required],
      alphacount_from: [p.alphacount_from ?? ''],
      self_billing_agreed_on: [p.self_billing_agreed_on ?? ''],
      note: [p.note ?? ''],
    });
    // Billed via alphacount: a cutover day is required (the backend refuses without).
    const cutover = this.form.get('alphacount_from')!;
    const applyCutoverRule = (via: string) => {
      cutover.setValidators(via === 'alphacount' ? Validators.required : null);
      cutover.updateValueAndValidity({ emitEvent: false });
    };
    applyCutoverRule(this.form.get('billing_via')!.value);
    this.form.get('billing_via')!.valueChanges.subscribe(applyCutoverRule);
  }

  get viaAlphacount(): boolean {
    return this.form?.get('billing_via')?.value === 'alphacount';
  }

  /** Empty dates go out as null, not "". */
  private body(v: any): any {
    return {
      ...v,
      payout_day: Number(v.payout_day),
      alphacount_from: v.alphacount_from || null,
      self_billing_agreed_on: v.self_billing_agreed_on || null,
    };
  }

  onSubmit() {
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const v = this.form.getRawValue();
    this.saving = true;
    const req$ = this.partner
      ? this.referralService.updatePartner({ ...this.partner, ...this.body(v), active: this.partner.active })
      : this.referralService.createPartner({ ...this.body(v), active: true });
    req$.subscribe({
        next: (saved) => {
          this.saving = false;
          this.dialogRef.close(saved);
        },
        error: (err) => {
          this.saving = false;
          const d = err?.error?.detail;
          if (err?.status === 409 && d?.existing_id) {
            this.conflict = { message: d.message, existingId: d.existing_id };
            return;
          }
          this.toastr.danger(
            (typeof d === 'string' ? d : d?.message) || this.translate.instant('dialogs.referralPartner.saveError'),
            ''
          );
        },
      });
  }

  /** Name taken: overwrite the existing partner with what was entered. */
  replaceExisting() {
    if (!this.conflict) return;
    const v = this.form.getRawValue();
    this.saving = true;
    this.referralService
      .updatePartner({ ...this.body(v), id: this.conflict.existingId, active: true })
      .subscribe({
        next: (saved) => {
          this.saving = false;
          this.dialogRef.close(saved);
        },
        error: (err) => {
          this.saving = false;
          this.toastr.danger(err?.error?.detail || this.translate.instant('dialogs.referralPartner.saveError'), '');
        },
      });
  }

  /** Name taken: keep the dialog, let the user pick another name. */
  chooseOtherName() {
    this.conflict = undefined;
    this.form.get('name')?.setValue(`${this.form.value.name}-2`);
  }
}
